import React from 'react';
import { useWallet } from '../contexts/WalletContext';
import WalletSessionModal from './WalletSessionModal';

/**
 * WalletConnect Component
 * Display wallet connection status, support manual reconnection
 */
export const WalletConnect: React.FC = () => {
  const {
    address,
    isConnected,
    chainId,
    balance,
    isLoading,
    error,
    connectWallet,
    isSessionModalOpen,
    closeSessionModal,
    sessionInfo,
  } = useWallet();

  // Loading status
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
        <span className="text-xs sm:text-sm text-blue-700 dark:text-blue-400">Connecting...</span>
      </div>
    );
  }

  // Error or disconnected state - show "Connect Wallet" button
  if (error || !isConnected || !address) {
    return (
      <>
        <button
          onClick={() => {
            console.log('[WalletConnect] 🔘 Connect Wallet button clicked');
            connectWallet();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-md"
          title="Click to connect wallet"
        >
          <span className="text-sm font-medium text-white">
            Connect Wallet
          </span>
        </button>
        {isSessionModalOpen && sessionInfo && (
          <WalletSessionModal onClose={closeSessionModal} />
        )}
      </>
    );
  }

  // 已连接状态
  const shortAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;
  const displayBalance = balance ? parseFloat(balance).toFixed(2) : '...';

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
        <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
        <div className="flex flex-col">
          <span className="text-sm font-mono font-medium text-green-700 dark:text-green-400">
            {shortAddress}
          </span>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">Balance:</span>
            <span className="text-base font-bold text-green-700 dark:text-green-400">
              {displayBalance} CHNX
            </span>
          </div>
        </div>
        {chainId && (
          <span className="ml-auto px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded hidden sm:inline">
            {chainId}
          </span>
        )}
      </div>
      {isSessionModalOpen && sessionInfo && (
        <WalletSessionModal onClose={closeSessionModal} />
      )}
    </>
  );
};

/**
 * WalletAddress Component  
 * Display address only (used in cards, etc.)
 */
export const WalletAddress: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { address, isConnected } = useWallet();

  if (!isConnected || !address) {
    return (
      <span className={`text-sm text-gray-400 ${className}`}>
        Not Connected
      </span>
    );
  }

  const shortAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;

  return (
    <span className={`text-sm font-mono ${className}`} title={address}>
      {shortAddress}
    </span>
  );
};
